import { createContext, useContext } from 'react';
import type { IActionDecision, IActionDiagnosticSnapshot } from '@models/documentSupport/ActionDiagnostics';

export type ActionDiagnosticReference = Pick<IActionDecision, 'kind' | 'objectId'> &
    Partial<Pick<IActionDecision, 'key' | 'location' | 'actionType' | 'displayName'>>;

export interface IDocumentSupportDiagnosticsContext {
    enabled: boolean;
    snapshot?: IActionDiagnosticSnapshot;
    selectAction: (action: ActionDiagnosticReference) => void;
    registerMountedActions: (registrationId: string, actions: ActionDiagnosticReference[]) => void;
    unregisterMountedActions: (registrationId: string) => void;
}

const getActionIdentity = (action: ActionDiagnosticReference): string => {
    if (action.key?.trim()) return action.key;
    return `fallback:${action.actionType ?? 'unknown'}:${action.displayName ?? 'unknown'}`;
};

export const getActionDiagnosticId = (action: ActionDiagnosticReference): string =>
    `${action.kind}:${action.objectId ?? 'document'}:${getActionIdentity(action)}:${action.location ?? 'anywhere'}`;

export const matchesActionDiagnostic = (action: IActionDecision, reference: ActionDiagnosticReference): boolean => {
    const identityMatches = action.key?.trim() || reference.key?.trim()
        ? action.key === reference.key
        : action.actionType === reference.actionType && action.displayName === reference.displayName;

    return (
        action.kind === reference.kind &&
        identityMatches &&
        action.objectId === reference.objectId &&
        (!reference.location || action.location === reference.location)
    );
};

export const DocumentSupportDiagnosticsContext = createContext<IDocumentSupportDiagnosticsContext>({
    enabled: false,
    selectAction: () => undefined,
    registerMountedActions: () => undefined,
    unregisterMountedActions: () => undefined,
});

export const useDocumentSupportDiagnostics = (): IDocumentSupportDiagnosticsContext =>
    useContext(DocumentSupportDiagnosticsContext);
